import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timeZoneLabel } from '@ruby-cms/shared';
import { Link } from 'react-router';
import { Button, Tag } from '@carbon/react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState, InlineError } from '../components/states';
import { Money, Pct, When, compactVnd } from '../components/format';
import { BarChart } from '../components/BarChart';
import { ExportButton } from '../components/Filters';
import { commandSummary, type Command } from './Commands';

type Health = { ok: boolean; error?: string };
type Status = {
  postgres: Health; redis: Health; heA: Health; gameDb: Health & { users?: number };
  worker: { ok: boolean; lastSeen: string | null };
};
type DayRow = { day: string; bet: string; win: string; refund: string; admin: string; ggr: string; players: number; rows: number };
type GameRow = Omit<DayRow, 'day'> & { game: string };
type Alert = { id: string; userId: number | null; nickname: string; game: string; amount: string; at: string };
type Dashboard = {
  today: DayRow; yesterday: DayRow; series: DayRow[]; games: GameRow[];
  online: { heA: number | null; heB: number | null; players: number } | null;
  commands: Record<string, number>;
  alerts: Alert[]; unackedAlerts: number;
  stats: { lagRows: number | null; updatedAt: string | null };
};

const ddmm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

function Change({ now, before }: { now: string; before: string }) {
  const a = BigInt(now), b = BigInt(before);
  if (a === b) return <span className="kpi__hint">bằng hôm qua</span>;
  const d = a - b;
  return <span className="kpi__hint">{d > 0n ? 'hơn' : 'kém'} hôm qua <span className="num">{compactVnd(d < 0n ? -d : d)}</span></span>;
}

function HealthStrip() {
  const q = useQuery({ queryKey: ['dashboard', 'status'], queryFn: () => api.get<Status>('/api/admin/system/status'), refetchInterval: 15_000 });
  const s = q.data;
  if (!s) return null;
  const parts: [string, boolean][] = [['Hệ A', s.heA.ok], ['SQLite game', s.gameDb.ok], ['Postgres', s.postgres.ok], ['Redis', s.redis.ok], ['Worker', s.worker.ok]];
  const bad = parts.filter(([, ok]) => !ok);
  return (
    <div className="row health">
      {bad.length ? bad.map(([l]) => <Tag key={l} type="red" size="sm">{l}: lỗi</Tag>) : <Tag type="green" size="sm">Mọi thành phần đang chạy</Tag>}
      <span className="muted">Worker chạy lần cuối <When v={s.worker.lastSeen} /></span>
    </div>
  );
}

function Alerts({ d }: { d: Dashboard }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/alerts/${id}/ack`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard'] }),
  });
  return (
    <section className="section section--flush panel">
      <div className="panel__head">
        <h2>Thắng lớn chưa xem</h2>
        {d.unackedAlerts ? <Tag type="magenta" size="sm">{d.unackedAlerts}</Tag> : null}
        <Link className="cell-link" to="/alerts">Lịch sử cảnh báo</Link>
      </div>
      {ack.error ? <InlineError error={ack.error} /> : null}
      {d.alerts.length ? (
        <ul className="list alerts">
          {d.alerts.map((a) => (
            <li key={a.id}>
              <div className="alerts__main">
                {a.userId && can('users.view') ? <Link to={`/users/${a.userId}`}>{a.nickname}</Link> : <span>{a.nickname}</span>}
                <span className="muted">{a.game} · <When v={a.at} /></span>
              </div>
              <Money v={a.amount} unit />
              <Button kind="ghost" size="sm" aria-label={`Đánh dấu đã xem thắng lớn của ${a.nickname}`} disabled={ack.isPending && ack.variables === a.id} onClick={() => ack.mutate(a.id)}>Đã xem</Button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Không có thắng lớn nào chờ xem" body={<>Mỗi lần trả thưởng từ ngưỡng trong {can('settings.manage') ? <Link to="/settings">Cài đặt</Link> : 'Cài đặt'} trở lên sẽ hiện ở đây.</>} />
      )}
    </section>
  );
}

// Lenh tao truoc khi bo buoc duyet (2026-09-29) van cho duyet: chi lenh do, moi lenh mot link toi trang ket qua
// cua no de duyet hoac tu choi (khong phai danh sach lenh: trang Hang lenh da bo, khach 2026-10-02)
function LegacyAwaiting({ count }: { count: number }) {
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['commands', 'awaiting'], enabled: count > 0 && can('commands.view'),
    queryFn: () => api.get<{ items: Command[] }>('/api/admin/commands', { status: 'AWAITING_APPROVAL', limit: 50 }),
  });
  if (!count || !can('commands.view')) return null;
  return (
    <section className="section panel">
      <div className="panel__head"><h2>Lệnh chờ duyệt từ trước ({count})</h2></div>
      <p className="muted">Lệnh tạo trước khi bỏ bước duyệt, chưa chạy. Mở từng lệnh để duyệt hoặc từ chối.</p>
      {q.error ? <InlineError error={q.error} /> : (
        <ul className="list">{(q.data?.items ?? []).map((c) => (
          <li key={c.id}><Link className="cell-link" to={`/commands/${c.id}`}>{c.type}</Link> {commandSummary(c)}
            {c.targetUserId != null ? <> · người chơi #{c.targetUserId}</> : null} · <When v={c.createdAt} /></li>
        ))}</ul>
      )}
    </section>
  );
}

export function HomePage() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['dashboard', 'main'], queryFn: () => api.get<Dashboard>('/api/admin/dashboard'), refetchInterval: 30_000 });
  const d = q.data;
  return (
    <div className="page">
      <PageHeader
        title="Tổng quan"
        lead={`Hôm nay theo ${timeZoneLabel()}, tiền tính bằng KRW (₩). Số liệu tự cập nhật khi có giao dịch mới.`}
        actions={<ExportButton path="/api/admin/dashboard" query={{}} />}
      />
      <HealthStrip />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : d ? (
        <>
          <div className="kpis kpis--home">
            <div className="kpi kpi--lead">
              <span className="kpi__label">Lãi nhà cái hôm nay</span>
              <span className="kpi__value"><Money v={d.today.ggr} signed unit /></span>
              <Change now={d.today.ggr} before={d.yesterday.ggr} />
            </div>
            <div className="kpi"><span className="kpi__label">Tiền cược</span><span className="kpi__value"><Money v={d.today.bet} unit /></span><span className="kpi__hint">tỷ lệ giữ lại <Pct num={d.today.ggr} den={d.today.bet} /></span></div>
            <div className="kpi"><span className="kpi__label">Trả thưởng</span><span className="kpi__value"><Money v={d.today.win} unit /></span><span className="kpi__hint">hoàn cược <span className="num">{compactVnd(d.today.refund)}</span></span></div>
            <div className="kpi"><span className="kpi__label">Người chơi hôm nay</span><span className="kpi__value kpi__value--gold">{d.today.players}</span><span className="kpi__hint">hôm qua {d.yesterday.players}</span></div>
            <div className="kpi">
              <span className="kpi__label">Đang online</span>
              <span className="kpi__value">{d.online ? d.online.players : '-'}</span>
              <span className="kpi__hint">{d.online ? <>kết nối hệ A {d.online.heA ?? '-'}, hệ B {d.online.heB ?? 'không trả lời'}</> : 'hệ A không trả lời'}</span>
            </div>
          </div>

          <LegacyAwaiting count={d.commands.AWAITING_APPROVAL ?? 0} />

          <div className="split">
            <section className="section section--flush panel">
              <div className="panel__head">
                <h2>Lãi nhà cái 14 ngày</h2>
                {can('reports.view') ? <Link to="/reports">Báo cáo chi tiết →</Link> : null}
              </div>
              {d.series.some((r) => r.rows > 0) ? (
                <BarChart title="Lãi nhà cái theo ngày" bars={d.series.map((r) => ({ key: r.day, label: ddmm(r.day), value: BigInt(r.ggr), hint: `${ddmm(r.day)}, ${r.players} người chơi` }))} />
              ) : (
                <EmptyState title="Chưa có giao dịch game nào" body="Biểu đồ hiện khi người chơi bắt đầu cược." />
              )}
              {d.stats.lagRows ? <p className="muted">Còn {d.stats.lagRows} dòng sổ chưa tính, worker đang xử lý.</p> : null}
            </section>
            <Alerts d={d} />
          </div>

          <section className="section">
            <h2>Theo game hôm nay</h2>
            {d.games.length ? (
              <table className="plain">
                <thead><tr><th>Game</th><th className="r">Cược</th><th className="r">Trả thưởng</th><th className="r">Hoàn</th><th className="r">Lãi</th><th className="r">Giữ lại</th><th className="r">Người chơi</th></tr></thead>
                <tbody>
                  {d.games.map((g) => (
                    <tr key={g.game}>
                      <td className="is-title">{g.game}</td>
                      <td className="r" data-label="Cược"><Money v={g.bet} /></td>
                      <td className="r" data-label="Trả thưởng"><Money v={g.win} /></td>
                      <td className="r" data-label="Hoàn"><Money v={g.refund} /></td>
                      <td className="r" data-label="Lãi"><Money v={g.ggr} signed /></td>
                      <td className="r" data-label="Giữ lại"><Pct num={g.ggr} den={g.bet} /></td>
                      <td className="r num" data-label="Người chơi">{g.players}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState title="Hôm nay chưa có ván nào" />
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
